/**
 * voice-v8 — Nutzerverwaltung (2026-07-29, Audit Paket F)
 *
 * Aus frontend/app.ts herausgeloest. Diese Datei hatte 18.303 Zeilen und war
 * damit weder ueberschaubar noch testbar — die uebrigen Ansichten liegen
 * laengst als eigene Module unter lib/ und haben dort Tests; nur der grosse
 * Rest in app.ts hatte keine.
 *
 * BEWUSST NUR VERSCHOBEN, NICHT UMGESCHRIEBEN. Der Sanierungsplan haelt fest:
 * Logik anfassen ohne Modul-Tests reproduziert genau die Drift-Fehler, die
 * das Audit gefunden hat. Der Inhalt ist unveraendert; was frueher aus dem
 * gemeinsamen Modul-Namensraum kam, wird jetzt ausdruecklich uebergeben —
 * und ist damit zum ersten Mal sichtbar.
 */

import { buildPageHeader } from "./sheet-ui.js";
import { zeichen } from "./zeichen.js";
import {
  geburtslandAnzeige,
  geburtslandFeldImRaster,
} from "./geburtsland-auswahl.js";

interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  createdAt: string | null;
  lastSeenAt: string | null;
  hasPassword: boolean;
  loginAllowed: boolean;
  birthday: string | null;
  birthTime: string | null;
  birthPlace: string | null;
  /** 24.09.2026, Ticket 2567: Geburtsland-Schlüssel; null = Österreich. */
  birthCountry?: string | null;
  tier: string;
}

export interface AdminUmgebung {
  /** DOM-Bezuege der App (Chat, Knoepfe, Eingabefeld …). */
  refs: Record<string, HTMLElement | null> & Record<string, unknown>;
  drawer: HTMLElement | null;
  scrollToBottomBtn: HTMLElement | null;
  connecting: () => boolean;
  unreadCount: () => number;
  closeDrawerForNavigation: () => void;
  openHumanDesignSheet: (target?: {
    userId: number;
    name: string;
  }) => void | Promise<void>;
  pinChatToBottom: () => void;
  prewarmChatSession: () => void;
  updateScrollBtnVisibility: () => void;
  updateUnreadBadge: () => void;
}

let umg: AdminUmgebung | null = null;

/** Einmalig beim Start setzen (aus app.ts). */
export function setzeAdminUmgebung(u: AdminUmgebung): void {
  umg = u;
}

function umgebung(): AdminUmgebung {
  if (!umg) throw new Error("AdminUmgebung nicht gesetzt");
  return umg;
}

function usrErrorMessage(code: string | undefined): string {
  switch (code) {
    case "bad_email":
      return "Ungültige E-Mail.";
    case "weak_password":
      return "Passwort zu kurz (min. 6).";
    case "email_exists":
      return "Diese E-Mail existiert bereits.";
    case "cannot_disable_self":
      return "Du kannst dir die Login-Freigabe nicht selbst entziehen.";
    case "cannot_delete_self":
      return "Du kannst dich nicht selbst löschen.";
    case "cannot_delete_admin":
      return "Der Haupt-Admin kann nicht gelöscht werden.";
    case "cannot_delete_last_user":
      return "Der letzte Nutzer kann nicht gelöscht werden.";
    case "not_found":
      return "Nutzer nicht gefunden.";
    case "bad_id":
      return "Ungültige Nutzer-ID.";
    case "forbidden":
      return "Kein Zugriff.";
    case "mail-failed":
      return "E-Mail-Versand fehlgeschlagen.";
    default:
      return "Etwas ist schiefgelaufen — bitte erneut versuchen.";
  }
}

function usrFormatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const now = Date.now();
  const diffMs = now - d.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diffMs >= 0 && diffMs < day) {
    const hours = Math.floor(diffMs / (60 * 60 * 1000));
    if (hours < 1) return "vor wenigen Minuten";
    return `vor ${hours} Std.`;
  }
  return usrDatum(d);
}

/**
 * DER Datumsstil dieser Seite.
 *
 * 2026-08-26 (Norm B8): Auf der Nutzerseite trafen DREI Formen aufeinander,
 * zwei davon in derselben Karte — "vor 3 Std." (zuletzt gesehen), "14.05.2026"
 * (aelter) und das rohe ISO-Datum "1970-05-14" beim Geburtstag. Ein Datum, das
 * aussieht wie eine Datenbankspalte, liest niemand.
 */
function usrDatum(d: Date): string {
  return d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export async function openUsersAdminSheet(): Promise<void> {
  // Drawer schließen (wie beim Human-Design-/„Mein Leben"-Sheet).
  // 2026-07-05 (Martin): der ECHTE Drawer laeuft ueber body.drawer-open —
  // Alt-Mechanismus schloss nichts, Menue blieb ueber dem Inhalt haengen.
  umgebung().closeDrawerForNavigation();
  document
    .getElementById("nav-menu-btn")
    ?.setAttribute("aria-expanded", "false");

  document.querySelector(".usr-overlay")?.remove();

  // 2026-07-21 (Audit NORM v3): page-sheet-Norm — EIN Kopfbau
  // (buildPageHeader), kein toter Overlay-Klick (Karte deckt alles),
  // Offset kommt aus der geteilten .page-sheet-Regel.
  const overlay = document.createElement("div");
  overlay.className = "usr-overlay page-sheet";

  const sheet = document.createElement("div");
  sheet.className = "usr-sheet";
  sheet.setAttribute("role", "dialog");
  sheet.setAttribute("aria-label", "Nutzer");

  const closeUsers = (): void => {
    overlay.remove();
    document.removeEventListener("keydown", onKey);
  };
  sheet.appendChild(buildPageHeader("Nutzer", closeUsers));

  // --- Globale Fehler-/Erfolgs-Leiste (oben, wie beim HD-Sheet) ---
  const errBar = document.createElement("div");
  errBar.className = "usr-error";
  errBar.hidden = true;
  sheet.appendChild(errBar);
  let noticeTimer: number | undefined;
  const showNotice = (msg: string, kind: "ok" | "err"): void => {
    errBar.textContent = msg;
    errBar.classList.toggle("usr-error--ok", kind === "ok");
    errBar.hidden = false;
    if (noticeTimer) window.clearTimeout(noticeTimer);
    if (kind === "ok") {
      noticeTimer = window.setTimeout(() => {
        errBar.hidden = true;
      }, 3500);
    }
  };

  // --- Body ---
  const body = document.createElement("div");
  body.className = "usr-body page-body";
  sheet.appendChild(body);

  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === "Escape") {
      overlay.remove();
      document.removeEventListener("keydown", onKey);
    }
  };
  document.addEventListener("keydown", onKey);
  sheet.querySelector<HTMLElement>(".page-head button")?.focus();

  // --- „Neuer Nutzer"-Formular (Card oben) ---
  const formCard = document.createElement("form");
  formCard.className = "usr-card usr-form";
  formCard.noValidate = true;
  const formTitle = document.createElement("h3");
  formTitle.className = "usr-card-title";
  formTitle.textContent = "Neuer Nutzer";
  formCard.appendChild(formTitle);

  const formGrid = document.createElement("div");
  formGrid.className = "usr-form-grid";
  const makeField = (
    labelText: string,
    type: string,
    placeholder = "",
  ): HTMLInputElement => {
    const wrap = document.createElement("label");
    wrap.className = "usr-field";
    const lab = document.createElement("span");
    lab.className = "usr-field-label";
    lab.textContent = labelText;
    const input = document.createElement("input");
    input.type = type;
    input.className = "usr-input";
    if (placeholder) input.placeholder = placeholder;
    wrap.appendChild(lab);
    wrap.appendChild(input);
    formGrid.appendChild(wrap);
    return input;
  };
  const emailInput = makeField("E-Mail", "email", "name@beispiel.de");
  emailInput.required = true;
  const nameInput = makeField("Name", "text", "optional");
  const birthdayInput = makeField("Geburtsdatum", "date");
  const birthTimeInput = makeField("Geburtszeit", "time");
  const birthPlaceInput = makeField("Geburtsort", "text", "z.B. Wien");
  // 24.09.2026, Ticket 2567: das Land bestimmt die Zeitzone des Charts.
  const birthCountrySelect = geburtslandFeldImRaster(formGrid);
  const passwordInput = makeField("Passwort", "password");
  passwordInput.placeholder = "optional — min. 6 Zeichen";
  formCard.appendChild(formGrid);

  const pwHint = document.createElement("p");
  pwHint.className = "usr-hint";
  pwHint.textContent =
    "Passwort optional (min. 6 Zeichen). Ohne Passwort meldet sich der Nutzer per E-Mail-Link an.";
  formCard.appendChild(pwHint);

  const loginRow = document.createElement("label");
  loginRow.className = "usr-check-row";
  const loginCheck = document.createElement("input");
  loginCheck.type = "checkbox";
  loginCheck.className = "usr-check";
  loginCheck.checked = true;
  const loginLabel = document.createElement("span");
  loginLabel.textContent = "Login erlaubt";
  loginRow.appendChild(loginCheck);
  loginRow.appendChild(loginLabel);
  formCard.appendChild(loginRow);

  const submitBtn = document.createElement("button");
  submitBtn.type = "submit";
  submitBtn.className = "usr-btn usr-btn--primary";
  submitBtn.textContent = "Nutzer anlegen";
  formCard.appendChild(submitBtn);

  // --- Liste (unter dem Formular) ---
  const listWrap = document.createElement("div");
  listWrap.className = "usr-list";

  // Pre.778: "Liste oben, Formular einklappbar". Vorher saß das Anlege-Formular
  // dauerhaft oben + drückte die Userliste nach unten. Jetzt: kompakter
  // "+ Neuer Nutzer"-Knopf, Formular klappt nur bei Bedarf auf → die Liste ist
  // prominent oben.
  const newUserToggle = document.createElement("button");
  newUserToggle.type = "button";
  newUserToggle.className = "usr-btn usr-btn--ghost usr-new-toggle";
  formCard.hidden = true;
  const setToggleLabel = (): void => {
    newUserToggle.textContent = formCard.hidden ? "+ Neu" : "Schließen";
  };
  setToggleLabel();
  newUserToggle.addEventListener("click", () => {
    formCard.hidden = !formCard.hidden;
    setToggleLabel();
    if (!formCard.hidden) emailInput.focus();
  });
  // NORM Regel 2 (2026-07-22, Martin "Header anpassen"): die EINE
  // Kopf-Aktion sitzt IM Kopf — vorher ueberlappte der Knopf den Titel.
  newUserToggle.classList.add("page-head-action");
  sheet.querySelector(".page-head")?.appendChild(newUserToggle);
  body.appendChild(formCard);
  body.appendChild(listWrap);

  let meId = 0;

  const renderListLoading = (): void => {
    listWrap.innerHTML = "";
    const load = document.createElement("div");
    load.className = "usr-list-state";
    const spinner = document.createElement("span");
    spinner.className = "book-spinner book-spinner--sm";
    spinner.setAttribute("aria-hidden", "true");
    const txt = document.createElement("span");
    txt.textContent = "Nutzer werden geladen …";
    load.appendChild(spinner);
    load.appendChild(txt);
    listWrap.appendChild(load);
  };

  const renderListMessage = (msg: string): void => {
    listWrap.innerHTML = "";
    const box = document.createElement("div");
    box.className = "usr-list-state";
    box.textContent = msg;
    listWrap.appendChild(box);
  };

  const renderUserRow = (u: AdminUser): HTMLElement => {
    const card = document.createElement("div");
    card.className = "usr-card usr-user";
    const isSelf = u.id === meId;
    const isAdmin = u.id === 1;

    // Kopfzeile: Name + Email + Badges + Aktionen.
    const top = document.createElement("div");
    top.className = "usr-user-top";

    const idcol = document.createElement("div");
    idcol.className = "usr-user-id";
    const nameEl = document.createElement("div");
    nameEl.className = "usr-user-name";
    nameEl.textContent = u.name?.trim() || "—";
    if (isSelf) {
      const meTag = document.createElement("span");
      meTag.className = "usr-me-tag";
      meTag.textContent = " (du)";
      nameEl.appendChild(meTag);
    }
    const emailEl = document.createElement("div");
    emailEl.className = "usr-user-email";
    emailEl.textContent = u.email;
    idcol.appendChild(nameEl);
    idcol.appendChild(emailEl);

    const badges = document.createElement("div");
    badges.className = "usr-user-badges";
    if (u.hasPassword) {
      const b = document.createElement("span");
      b.className = "usr-badge";
      b.textContent = "Passwort";
      badges.appendChild(b);
    }
    idcol.appendChild(badges);

    // Pre.778: Geburtsdaten direkt in der Karte sichtbar — so sieht man den
    // Stand (und Tippfehler) sofort, ohne erst „Bearbeiten" zu öffnen.
    const birth = document.createElement("div");
    birth.className = "usr-user-birth";
    const bd = u.birthday?.trim();
    const bt = u.birthTime?.trim();
    const bp = u.birthPlace?.trim();
    // 24.09.2026, Ticket 2567: das Land (Zeitzone des Charts) mit anzeigen —
    // ohne gesetztes Land gilt Österreich, das steht dann auch da.
    const land = geburtslandAnzeige(u.birthCountry) || "Österreich (Vorgabe)";
    const bdLesbar = bd && !Number.isNaN(new Date(bd).getTime()) ? usrDatum(new Date(bd)) : bd;
    birth.textContent = bd
      ? `Geboren: ${bdLesbar}${bt ? " · " + bt : ""}${bp ? " · " + bp : ""} · ${land}`
      : "Keine Geburtsdaten";
    idcol.appendChild(birth);

    top.appendChild(idcol);

    // Meta: zuletzt gesehen + Login-Toggle.
    const meta = document.createElement("div");
    meta.className = "usr-user-meta";
    const seen = document.createElement("div");
    seen.className = "usr-user-seen";
    seen.textContent = `zuletzt gesehen: ${usrFormatDate(u.lastSeenAt)}`;
    meta.appendChild(seen);

    const loginToggleRow = document.createElement("label");
    loginToggleRow.className = "usr-check-row usr-check-row--inline";
    const loginToggle = document.createElement("input");
    loginToggle.type = "checkbox";
    loginToggle.className = "usr-check";
    loginToggle.checked = u.loginAllowed;
    const loginToggleLabel = document.createElement("span");
    loginToggleLabel.textContent = "Login erlaubt";
    loginToggleRow.appendChild(loginToggle);
    loginToggleRow.appendChild(loginToggleLabel);
    meta.appendChild(loginToggleRow);

    const tierRow = document.createElement("label");
    tierRow.className = "usr-check-row usr-check-row--inline usr-tier-row";
    const tierLbl = document.createElement("span");
    tierLbl.textContent = "Paket";
    const tierSel = document.createElement("select");
    tierSel.className = "usr-tier-select";
    // 23.09.2026, Ticket 2529: Abschrift von STUFEN in server/entitlements.ts
    // (das Frontend darf die Datei nicht laden — sie trägt die Budgets). Fehlt
    // hier eine Stufe, ist keine Option gewählt, der Browser zeigt „Frei",
    // und ein Klick stuft herab.
    const TIER_OPTS: Array<[string, string]> = [["free", "Frei"], ["begleiter", "Begleiter"], ["lebensbuch", "Lebensbuch"], ["spur", "Spur"], ["weg", "Weg"], ["revier", "Revier"]];
    for (const [val, label] of TIER_OPTS) {
      const o = document.createElement("option");
      o.value = val;
      o.textContent = label;
      if ((u.tier || "free") === val) o.selected = true;
      tierSel.appendChild(o);
    }
    if (isAdmin) { tierSel.disabled = true; tierSel.title = "Admin hat immer vollen Zugriff"; }
    tierRow.append(tierLbl, tierSel);
    meta.appendChild(tierRow);
    tierSel.addEventListener("change", () => {
      tierSel.disabled = true;
      void (async () => {
        try {
          const res = await fetch(`/api/admin/users/${u.id}/tier`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ tier: tierSel.value }),
          });
          if (!res.ok) throw new Error("tier");
        } catch {
          /* belassen */
        } finally {
          tierSel.disabled = isAdmin;
        }
      })();
    });

    top.appendChild(meta);

    card.appendChild(top);

    loginToggle.addEventListener("change", () => {
      loginToggle.disabled = true;
      void (async () => {
        try {
          const res = await fetch(`/api/admin/users/${u.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify({ loginAllowed: loginToggle.checked }),
          });
          const data = (await res.json().catch(() => null)) as {
            ok?: boolean;
            error?: string;
          } | null;
          if (!res.ok || !data || data.ok === false) {
            loginToggle.checked = !loginToggle.checked; // zurückrollen
            showNotice(usrErrorMessage(data?.error), "err");
            return;
          }
          u.loginAllowed = loginToggle.checked;
          showNotice("Login-Freigabe aktualisiert.", "ok");
        } catch {
          loginToggle.checked = !loginToggle.checked;
          showNotice("Keine Verbindung — bitte erneut versuchen.", "err");
        } finally {
          loginToggle.disabled = false;
        }
      })();
    });

    // Aktions-Zeile.
    const actionsRow = document.createElement("div");
    actionsRow.className = "usr-user-actions";

    // Human Design dieses Nutzers ansehen (Admin-Sicht). Schließt die
    // Nutzer-Liste und öffnet das HD-Sheet im Ziel-Modus (?userId=…).
    const hdBtn = document.createElement("button");
    hdBtn.type = "button";
    hdBtn.className = "usr-btn usr-btn--ghost";
    // 2026-08-26 (Norm A): war ein handgeschriebenes, GEFUELLTES Sonnen-SVG
    // per innerHTML — das einzige Zeichen der App mit fremder Machart
    // (kein 24er-Raster, keine Strichstaerke, nicht einfaerbbar wie die
    // uebrigen). Jetzt aus dem gemeinsamen Vorrat.
    hdBtn.innerHTML = `${zeichen("stern", 15)}<span>Human Design</span>`;
    hdBtn.addEventListener("click", () => {
      overlay.remove();
      void umgebung().openHumanDesignSheet({ userId: u.id, name: u.name || u.email });
    });
    actionsRow.appendChild(hdBtn);

    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "usr-btn usr-btn--ghost";
    editBtn.textContent = "Bearbeiten";
    actionsRow.appendChild(editBtn);

    const linkBtn = document.createElement("button");
    linkBtn.type = "button";
    linkBtn.className = "usr-btn usr-btn--ghost";
    linkBtn.textContent = "Login-Link senden";
    actionsRow.appendChild(linkBtn);

    let delBtn: HTMLButtonElement | null = null;
    if (!isSelf && !isAdmin) {
      delBtn = document.createElement("button");
      delBtn.type = "button";
      delBtn.className = "usr-btn usr-btn--danger";
      delBtn.textContent = "Löschen";
      actionsRow.appendChild(delBtn);
    }
    card.appendChild(actionsRow);
    // 2026-07-22 (Martin, "kompakt und uebersichtlich"): Zeile statt
    // Riesen-Karte — Details klappen erst beim Antippen der Zeile auf.
    card.classList.add("usr-user--zeile");
    top.classList.add("usr-user-toprow");
    top.addEventListener("click", (ev) => {
      if (
        (ev.target as HTMLElement).closest("button,input,select,a,label")
      ) {
        return;
      }
      card.classList.toggle("usr-user--offen");
    });

    // --- Inline-Edit (eingeklappt) ---
    const editPanel = document.createElement("div");
    editPanel.className = "usr-edit";
    editPanel.hidden = true;
    const editGrid = document.createElement("div");
    editGrid.className = "usr-form-grid";
    const makeEdit = (
      labelText: string,
      type: string,
      value: string,
      placeholder = "",
    ): HTMLInputElement => {
      const w = document.createElement("label");
      w.className = "usr-field";
      const l = document.createElement("span");
      l.className = "usr-field-label";
      l.textContent = labelText;
      const inp = document.createElement("input");
      inp.type = type;
      inp.className = "usr-input";
      inp.value = value;
      if (placeholder) inp.placeholder = placeholder;
      w.appendChild(l);
      w.appendChild(inp);
      editGrid.appendChild(w);
      return inp;
    };
    const eName = makeEdit("Name", "text", u.name ?? "");
    const eBirthday = makeEdit("Geburtsdatum", "date", u.birthday ?? "");
    const eBirthTime = makeEdit("Geburtszeit", "time", u.birthTime ?? "");
    const eBirthPlace = makeEdit("Geburtsort", "text", u.birthPlace ?? "");
    const eBirthCountry = geburtslandFeldImRaster(editGrid, u.birthCountry);
    const ePassword = makeEdit("Neues Passwort", "password", "");
    ePassword.placeholder = "leer lassen = unverändert";
    editPanel.appendChild(editGrid);

    const editActions = document.createElement("div");
    editActions.className = "usr-edit-actions";
    const saveBtn = document.createElement("button");
    saveBtn.type = "button";
    saveBtn.className = "usr-btn usr-btn--primary";
    saveBtn.textContent = "Speichern";
    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.className = "usr-btn usr-btn--ghost";
    cancelBtn.textContent = "Abbrechen";
    editActions.appendChild(saveBtn);
    editActions.appendChild(cancelBtn);
    editPanel.appendChild(editActions);
    card.appendChild(editPanel);

    editBtn.addEventListener("click", () => {
      editPanel.hidden = !editPanel.hidden;
    });
    cancelBtn.addEventListener("click", () => {
      editPanel.hidden = true;
    });

    saveBtn.addEventListener("click", () => {
      const payload: Record<string, string> = {
        name: eName.value.trim(),
        birthday: eBirthday.value,
        birthTime: eBirthTime.value,
        birthPlace: eBirthPlace.value.trim(),
        birthCountry: eBirthCountry.value,
      };
      const pw = ePassword.value;
      if (pw.length > 0) payload.password = pw;
      saveBtn.disabled = true;
      void (async () => {
        try {
          const res = await fetch(`/api/admin/users/${u.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
            body: JSON.stringify(payload),
          });
          const data = (await res.json().catch(() => null)) as {
            ok?: boolean;
            error?: string;
          } | null;
          if (!res.ok || !data || data.ok === false) {
            showNotice(usrErrorMessage(data?.error), "err");
            return;
          }
          showNotice("Gespeichert.", "ok");
          editPanel.hidden = true;
          await reload();
        } catch {
          showNotice("Keine Verbindung — bitte erneut versuchen.", "err");
        } finally {
          saveBtn.disabled = false;
        }
      })();
    });

    linkBtn.addEventListener("click", () => {
      linkBtn.disabled = true;
      void (async () => {
        try {
          const res = await fetch(`/api/admin/users/${u.id}/magic-link`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "same-origin",
          });
          const data = (await res.json().catch(() => null)) as {
            ok?: boolean;
            error?: string;
          } | null;
          if (!res.ok || !data || data.ok === false) {
            showNotice(usrErrorMessage(data?.error), "err");
            return;
          }
          showNotice("Login-Link verschickt.", "ok");
        } catch {
          showNotice("Keine Verbindung — bitte erneut versuchen.", "err");
        } finally {
          linkBtn.disabled = false;
        }
      })();
    });

    // --- Inline-Lösch-Bestätigung (kein native confirm) ---
    if (delBtn) {
      const confirmRow = document.createElement("div");
      confirmRow.className = "usr-confirm";
      confirmRow.hidden = true;
      const confirmText = document.createElement("span");
      confirmText.className = "usr-confirm-text";
      confirmText.textContent = "Wirklich löschen?";
      const yesBtn = document.createElement("button");
      yesBtn.type = "button";
      yesBtn.className = "usr-btn usr-btn--danger";
      yesBtn.textContent = "Ja";
      const noBtn = document.createElement("button");
      noBtn.type = "button";
      noBtn.className = "usr-btn usr-btn--ghost";
      noBtn.textContent = "Abbrechen";
      confirmRow.appendChild(confirmText);
      confirmRow.appendChild(yesBtn);
      confirmRow.appendChild(noBtn);
      card.appendChild(confirmRow);

      const theDelBtn = delBtn;
      theDelBtn.addEventListener("click", () => {
        confirmRow.hidden = false;
        theDelBtn.hidden = true;
      });
      noBtn.addEventListener("click", () => {
        confirmRow.hidden = true;
        theDelBtn.hidden = false;
      });
      yesBtn.addEventListener("click", () => {
        yesBtn.disabled = true;
        noBtn.disabled = true;
        void (async () => {
          try {
            const res = await fetch(`/api/admin/users/${u.id}`, {
              method: "DELETE",
              credentials: "same-origin",
            });
            const data = (await res.json().catch(() => null)) as {
              ok?: boolean;
              error?: string;
            } | null;
            if (!res.ok || !data || data.ok === false) {
              showNotice(usrErrorMessage(data?.error), "err");
              confirmRow.hidden = true;
              theDelBtn.hidden = false;
              return;
            }
            showNotice("Nutzer gelöscht.", "ok");
            await reload();
          } catch {
            showNotice("Keine Verbindung — bitte erneut versuchen.", "err");
            confirmRow.hidden = true;
            theDelBtn.hidden = false;
          } finally {
            yesBtn.disabled = false;
            noBtn.disabled = false;
          }
        })();
      });
    }

    return card;
  };

  const renderList = (users: AdminUser[]): void => {
    listWrap.innerHTML = "";
    if (users.length === 0) {
      renderListMessage("Noch keine Nutzer angelegt.");
      return;
    }
    for (const u of users) listWrap.appendChild(renderUserRow(u));
  };

  const reload = async (): Promise<void> => {
    renderListLoading();
    try {
      const res = await fetch("/api/admin/users", {
        credentials: "same-origin",
      });
      const data = (await res.json().catch(() => null)) as {
        ok?: boolean;
        users?: AdminUser[];
        me?: number;
        error?: string;
      } | null;
      if (res.status === 403) {
        renderListMessage("Kein Zugriff — nur für Administratoren.");
        return;
      }
      if (!res.ok || !data || data.ok === false || !Array.isArray(data.users)) {
        renderListMessage(usrErrorMessage(data?.error));
        return;
      }
      meId = typeof data.me === "number" ? data.me : 0;
      renderList(data.users);
    } catch {
      renderListMessage("Keine Verbindung — bitte später erneut versuchen.");
    }
  };

  formCard.addEventListener("submit", (ev) => {
    ev.preventDefault();
    const email = emailInput.value.trim().toLowerCase();
    if (!email?.includes("@")) {
      showNotice("Ungültige E-Mail.", "err");
      emailInput.focus();
      return;
    }
    const payload: Record<string, string | boolean> = {
      email,
      loginAllowed: loginCheck.checked,
    };
    const name = nameInput.value.trim();
    if (name) payload.name = name;
    if (birthdayInput.value) payload.birthday = birthdayInput.value;
    if (birthTimeInput.value) payload.birthTime = birthTimeInput.value;
    const place = birthPlaceInput.value.trim();
    if (place) payload.birthPlace = place;
    payload.birthCountry = birthCountrySelect.value;
    const pw = passwordInput.value;
    if (pw.length > 0) payload.password = pw;

    submitBtn.disabled = true;
    void (async () => {
      try {
        const res = await fetch("/api/admin/users", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "same-origin",
          body: JSON.stringify(payload),
        });
        const data = (await res.json().catch(() => null)) as {
          ok?: boolean;
          error?: string;
        } | null;
        if (!res.ok || !data || data.ok === false) {
          showNotice(usrErrorMessage(data?.error), "err");
          return;
        }
        showNotice("Nutzer angelegt.", "ok");
        formCard.reset();
        loginCheck.checked = true;
        await reload();
      } catch {
        showNotice("Keine Verbindung — bitte erneut versuchen.", "err");
      } finally {
        submitBtn.disabled = false;
      }
    })();
  });

  await reload();
}
